import { useForm } from "@inertiajs/react";
import { Box, Container, Heading, Button, Text, VStack, Input, HStack, Select, Card, Stack, createListCollection } from "@chakra-ui/react";
import { useState } from "react";
import { event_game_path } from "@/routes";
import { useAuth } from "@/hooks/useAuth";
import { Player } from "@/types";

interface Event {
  id: number;
  name: string;
}

interface GameData {
  id: number;
  player1: {
    player_id: string;
    point: number;
  };
  player2: {
    player_id: string;
    point: number;
  };
}

interface Props {
  event_id: number;
  game_id: number;
  event: Event;
  players: Player[];
  game: GameData;
}

interface PlayerSearchResult {
  player_id: string;
  display_name: string;
}

export default function Edit({ event_id, game_id, event, players, game }: Props) {
  const currentPlayer = useAuth().player!;
  const { data, setData, patch, processing, errors } = useForm({
    player1_player_id: game.player1.player_id,
    player2_player_id: game.player2.player_id,
    player1_point: game.player1.point.toString(),
    player2_point: game.player2.point.toString(),
  });

  const [player1Results, setPlayer1Results] = useState<PlayerSearchResult[]>([]);
  const [player2Results, setPlayer2Results] = useState<PlayerSearchResult[]>([]);
  
  // 初期プレイヤー名を設定
  const findPlayerName = (playerId: string) => {
    const player = players.find(p => p.player_id === playerId);
    return player?.display_name || "";
  };
  
  const [player1Name, setPlayer1Name] = useState(findPlayerName(game.player1.player_id));
  const [player2Name, setPlayer2Name] = useState(findPlayerName(game.player2.player_id));
  const [showPlayer1Results, setShowPlayer1Results] = useState(false);
  const [showPlayer2Results, setShowPlayer2Results] = useState(false);
  const [animatingButtons, setAnimatingButtons] = useState<Set<string>>(new Set());

  const searchPlayers = async (query: string): Promise<PlayerSearchResult[]> => {
    console.log("Searching players with query:", query);
    if (!query.trim()) return [];

    try {
      const url = new URL("/players/search", window.location.origin);
      url.searchParams.append("query", query);
      if (event_id) {
        url.searchParams.append("event_id", event_id.toString());
      }

      const response = await fetch(url.toString());
      const result = await response.json();
      console.log("Player search results:", result);
      return result.players || [];
    } catch (error) {
      console.error("Player search failed:", error);
      return [];
    }
  };

  const handlePlayer1Search = async (value: string) => {
    setData("player1_player_id", value);
    if (value.length >= 1) {
      const results = await searchPlayers(value);
      setPlayer1Results(results);
      setShowPlayer1Results(true);

      // 完全一致の場合は名前を設定
      const exactMatch = results.find(p => p.player_id === value);
      if (exactMatch) {
        setPlayer1Name(exactMatch.display_name);
      } else {
        setPlayer1Name("");
      }
    } else {
      setPlayer1Results([]);
      setShowPlayer1Results(false);
      setPlayer1Name("");
    }
  };

  const handlePlayer2Search = async (value: string) => {
    setData("player2_player_id", value);
    if (value.length >= 1) {
      const results = await searchPlayers(value);
      setPlayer2Results(results);
      setShowPlayer2Results(true);

      // 完全一致の場合は名前を設定
      const exactMatch = results.find(p => p.player_id === value);
      if (exactMatch) {
        setPlayer2Name(exactMatch.display_name);
      } else {
        setPlayer2Name("");
      }
    } else {
      setPlayer2Results([]);
      setShowPlayer2Results(false);
      setPlayer2Name("");
    }
  };

  const selectPlayer1 = (player: PlayerSearchResult) => {
    const buttonId = `player1-${player.player_id}`;
    setAnimatingButtons(prev => new Set(prev).add(buttonId));

    setData("player1_player_id", player.player_id);
    setPlayer1Name(player.display_name);
    setPlayer1Results([]);
    setShowPlayer1Results(false);

    // アニメーションを1秒後に停止
    setTimeout(() => {
      setAnimatingButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(buttonId);
        return newSet;
      });
    }, 1000);
  };

  const selectPlayer2 = (player: PlayerSearchResult) => {
    const buttonId = `player2-${player.player_id}`;
    setAnimatingButtons(prev => new Set(prev).add(buttonId));

    setData("player2_player_id", player.player_id);
    setPlayer2Name(player.display_name);
    setPlayer2Results([]);
    setShowPlayer2Results(false);

    // アニメーションを1秒後に停止
    setTimeout(() => {
      setAnimatingButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(buttonId);
        return newSet;
      });
    }, 1000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch(event_game_path(event_id, game_id));
  };

  const pointOptions = createListCollection({
    items: Array.from({ length: 13 }, (_, i) => ({
      label: i.toString(),
      value: i.toString(),
    })),
  });

  return (
    <Container maxW="4xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="2xl" textAlign="center">
            試合結果の編集
          </Heading>
          {event && (
            <Heading size="lg" textAlign="center" color="gray.600" mt={2}>
              {event.name}
            </Heading>
          )}
        </Box>

        {/* Error Messages */}
        {Object.keys(errors).length > 0 && (
          <Box bg="red.50" border="1px" borderColor="red.200" rounded="md" p={4}>
            <Text color="red.700" fontWeight="medium" mb={2}>
              エラーが発生しました：
            </Text>
            {Object.entries(errors).map(([key, messages]) => (
              <Text key={key} color="red.700" fontSize="sm">
                {Array.isArray(messages) ? messages.join(", ") : messages}
              </Text>
            ))}
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <Stack gap={8}>
            <HStack gap={8} align="start">
              {/* Player 1 */}
              <Card.Root flex={1} bg="gray.50">
                <Card.Header pb={4}>
                  <Heading size="md" textAlign="center">
                    プレイヤー1
                  </Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <Stack gap={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        プレイヤーID
                      </Text>
                      <Box position="relative">
                        <Input
                          value={data.player1_player_id}
                          onChange={e => handlePlayer1Search(e.target.value)}
                          placeholder="プレイヤーIDを入力"
                          autoComplete="off"
                        />
                        {showPlayer1Results && player1Results.length > 0 && (
                          <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            bg="white"
                            border="1px"
                            borderColor="gray.200"
                            rounded="md"
                            shadow="lg"
                            maxH="200px"
                            overflowY="auto"
                            zIndex={10}
                          >
                            {player1Results.map(player => (
                              <Box key={player.player_id} p={2} cursor="pointer" _hover={{ bg: "gray.100" }} onClick={() => selectPlayer1(player)}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {player.display_name}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  ID: {player.player_id}
                                </Text>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Text fontSize="sm">
                      プレイヤー名:{" "}
                      <Text as="span" fontWeight="medium">
                        {player1Name || "未選択"}
                      </Text>
                    </Text>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        得点
                      </Text>
                      <Select.Root
                        value={[data.player1_point.toString()]}
                        onValueChange={e => setData("player1_point", e.value[0] || "")}
                        collection={pointOptions}
                      >
                        <Select.Trigger>
                          <Select.ValueText placeholder="選択してください" />
                        </Select.Trigger>
                        <Select.Content>
                          {pointOptions.items.map(option => (
                            <Select.Item key={option.value} item={option}>
                              {option.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </Box>
                  </Stack>
                </Card.Body>
              </Card.Root>

              {/* Player 2 */}
              <Card.Root flex={1} bg="gray.50">
                <Card.Header pb={4}>
                  <Heading size="md" textAlign="center">
                    プレイヤー2
                  </Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <Stack gap={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        プレイヤーID
                      </Text>
                      <Box position="relative">
                        <Input
                          value={data.player2_player_id}
                          onChange={e => handlePlayer2Search(e.target.value)}
                          placeholder="プレイヤーIDを入力"
                          autoComplete="off"
                        />
                        {showPlayer2Results && player2Results.length > 0 && (
                          <Box
                            position="absolute"
                            top="100%"
                            left={0}
                            right={0}
                            bg="white"
                            border="1px"
                            borderColor="gray.200"
                            rounded="md"
                            shadow="lg"
                            maxH="200px"
                            overflowY="auto"
                            zIndex={10}
                          >
                            {player2Results.map(player => (
                              <Box key={player.player_id} p={2} cursor="pointer" _hover={{ bg: "gray.100" }} onClick={() => selectPlayer2(player)}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {player.display_name}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  ID: {player.player_id}
                                </Text>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    <Text fontSize="sm">
                      プレイヤー名:{" "}
                      <Text as="span" fontWeight="medium">
                        {player2Name || "未選択"}
                      </Text>
                    </Text>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        得点
                      </Text>
                      <Select.Root
                        value={[data.player2_point.toString()]}
                        onValueChange={e => setData("player2_point", e.value[0] || "")}
                        collection={pointOptions}
                      >
                        <Select.Trigger>
                          <Select.ValueText placeholder="選択してください" />
                        </Select.Trigger>
                        <Select.Content>
                          {pointOptions.items.map(option => (
                            <Select.Item key={option.value} item={option}>
                              {option.label}
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </Box>
                  </Stack>
                </Card.Body>
              </Card.Root>
            </HStack>

            <Box textAlign="center">
              <Button type="submit" size="lg" colorPalette="blue" loading={processing} px={8}>
                試合結果を更新
              </Button>
            </Box>
          </Stack>
        </form>

        {/* プレイヤー一覧 */}
        {players && players.length > 0 && (
          <Box>
            <Heading size="lg" mb={4}>
              プレイヤー一覧
            </Heading>
            <Card.Root>
              <Card.Body>
                <Stack gap={2}>
                  {players.map((player, index) => (
                    <Box key={player.player_id} p={3} bg={index % 2 === 0 ? "gray.50" : "white"} rounded="md" border="1px" borderColor="gray.200">
                      <HStack justify="space-between">
                        <Box>
                          <Text fontWeight="medium">{player.display_name}</Text>
                          <Text fontSize="sm" color="gray.500">
                            ID: {player.player_id}
                          </Text>
                        </Box>
                        <HStack gap={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => selectPlayer1(player)}
                            colorPalette={animatingButtons.has(`player1-${player.player_id}`) ? "green" : "gray"}
                            transform={animatingButtons.has(`player1-${player.player_id}`) ? "scale(1.05)" : "scale(1)"}
                            transition="all 0.2s ease-in-out"
                            _hover={{
                              transform: "scale(1.02)",
                              bg: animatingButtons.has(`player1-${player.player_id}`) ? "green.100" : "gray.100",
                            }}
                          >
                            プレイヤー1に設定
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => selectPlayer2(player)}
                            colorPalette={animatingButtons.has(`player2-${player.player_id}`) ? "green" : "gray"}
                            transform={animatingButtons.has(`player2-${player.player_id}`) ? "scale(1.05)" : "scale(1)"}
                            transition="all 0.2s ease-in-out"
                            _hover={{
                              transform: "scale(1.02)",
                              bg: animatingButtons.has(`player2-${player.player_id}`) ? "green.100" : "gray.100",
                            }}
                          >
                            プレイヤー2に設定
                          </Button>
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                </Stack>
              </Card.Body>
            </Card.Root>
          </Box>
        )}
      </VStack>
    </Container>
  );
}